import { CircleX, FilePlus, LoaderCircle, UploadIcon } from "lucide-react";
import React, { useCallback, useState, useEffect, useRef } from "react";
import HiddenInput from "./HiddenInput";
import FpButton from "../../FpButton";
import useFileTrayStore, { FileWithStatus } from "./useFileTrayStore";
import uploadFiles from "./services/uploadFiles";
import { v4 as uuidv4 } from "uuid";
import { queryClient } from "@/ReactQuery/reactQueryClient";
const UploadStuff = ({
  prependFilePath = [],
  selectables = ["file", "folder"],
  multiple = true,
  metaData = {},
  closeDialog = () => {},
  driveSelection = false,
}: {
  prependFilePath?: string[];
  selectables?: ("file" | "folder")[];
  multiple?: boolean;
  metaData?: {
    orgId?: string;
    workspaceId?: string;
    useFilesystem?: boolean;
    driveId?: string;
  };
  closeDialog?: () => void;
  driveSelection?: boolean;
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showSelectedFiles, setShowSelectedFiles] = useState(false);
  const [selectedDriveId, setSelectedDriveId] = useState<string>("");
  const addFile = useFileTrayStore((state) => state.addFile);
  const updateFile = useFileTrayStore((state) => state.updateFile);

  // Refs for file inputs to clear them when needed
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const driveList: any[] = [];

  // Set initial drive selection when drive list is available
  useEffect(() => {
    if (
      driveSelection &&
      driveList.length > 0 &&
      (!selectedDriveId || selectedDriveId === "")
    ) {
      setSelectedDriveId(driveList[0]._id);
    }
  }, [driveSelection, driveList, selectedDriveId]);

  // Ensure we always have a selected drive when drive selection is enabled
  useEffect(() => {
    if (driveSelection && driveList.length > 0 && selectedDriveId === "") {
      setSelectedDriveId(driveList[0]._id);
    }
  }, [driveSelection, driveList, selectedDriveId]);

  const removeFile = useCallback(
    (index: number) => {
      setSelectedFiles((prevFiles) => {
        const newFiles = prevFiles.filter((_, i) => i !== index);

        // Clear file inputs when the last file is removed
        if (newFiles.length === 0) {
          if (fileInputRef.current) fileInputRef.current.value = "";
          if (folderInputRef.current) folderInputRef.current.value = "";
        }

        return newFiles;
      });
    },
    [selectedFiles.length]
  );

  const appendSelectedFiles = useCallback((files: File[]) => {
    files = files.filter((file) => !file.name.startsWith("."));

    if (files.length > 0) {
      setShowSelectedFiles(true);
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    }
  }, []);

  // Ensure showSelectedFiles is synchronized with selectedFiles
  useEffect(() => {
    if (selectedFiles.length > 0 && !showSelectedFiles) {
      setShowSelectedFiles(true);
    } else if (selectedFiles.length === 0 && showSelectedFiles) {
      setShowSelectedFiles(false);
    }
  }, [selectedFiles.length]);

  const addFilesToTray = useCallback(
    async (files: File[]) => {
      const trayFiles = files.map((file) => ({
        ...file,
        name: file.name,
        tempId: uuidv4(),
        status: "pending",
        originalFile: file,
      }));

      for (const file of trayFiles) {
        addFile(file as FileWithStatus);
      }

      if (closeDialog) {
        closeDialog();
      }

      // Include selected drive in metadata if drive selection is enabled
      // If driveSelection is enabled but no drives are available (driveList is empty),
      // fall back to uploading without a driveId
      const effectiveDriveId =
        driveSelection && selectedDriveId ? selectedDriveId : metaData.driveId;

      const updatedMetaData = {
        ...metaData,
        driveId: effectiveDriveId,
      };

      const uploadedFiles = await uploadFiles(
        files,
        prependFilePath,
        updatedMetaData,
        trayFiles as FileWithStatus[]
      );

      for (const index in uploadedFiles) {
        updateFile({
          ...trayFiles[index],
          ...uploadedFiles[index],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["orgFileList"],
      });
    },
    [updateFile, driveSelection, selectedDriveId, metaData]
  );

  return (
    <div className="w-full h-full max-w-[500px] ">
      {driveSelection && driveList.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Drive
          </label>
          <select
            value={selectedDriveId}
            onChange={(e) => {
              setSelectedDriveId(e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            {driveList.map((drive) => (
              <option key={drive._id} value={drive._id}>
                {drive.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {showSelectedFiles && (
        <div className="w-full max-h-[200px] overflow-auto px-2 mt-4 shadow-inner rounded-md border pb-2">
          {selectedFiles.map((file, index) => (
            <div
              className="p-2 bg-gray-200 mt-2 rounded-md flex items-center justify-between text-[10px] w-full"
              key={index}
            >
              <div className="flex items-center">
                <span className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {file.name}
                </span>
                <span className="ml-2">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <CircleX
                onClick={() => {
                  removeFile(index);
                }}
                className="w-3 h-3 cursor-pointer hover:text-red-500"
              />
            </div>
          ))}
        </div>
      )}

      {showSelectedFiles && (
        <div className="w-full flex justify-center mt-4 mb-4">
          {/* <FpButton
            variant="outlined"
            onClick={() => {
              setSelectedFiles([]);
              setShowSelectedFiles(false);
            }}
            style={{
              fontSize: "10px",
              padding: "3px 8px",
              backgroundColor: "",
              marginRight: "20px",
              color: "#f55",
              borderColor: "#f55",
            }}
          >
            <CircleX className="w-3 h-3 mr-2" />
            Remove All
          </FpButton> */}
          {/* <FpButton
            variant="outlined"
            onClick={() => {
              setShowSelectedFiles(false);
            }}
            style={{
              fontSize: "10px",
              padding: "3px 8px",
              backgroundColor: "",
              marginRight: "20px",
            }}
          >
            <FilePlus className="w-3 h-3 mr-2" />
            Choose More
          </FpButton> */}
          <FpButton
            variant="contained"
            onClick={() => {
              addFilesToTray(selectedFiles);
              setSelectedFiles([]);
              setShowSelectedFiles(false);
            }}
            style={{
              fontSize: "10px",
              padding: "3px 8px",
              backgroundColor: "",
            }}
          >
            <UploadIcon className="w-3 h-3 mr-2" />
            Upload {selectedFiles.length} Files
          </FpButton>
        </div>
      )}

      <div className="p-2">
        <div className="flex w-full my-2 mb-4 text-[12px] justify-around">
          {selectables.includes("file") && (
            <HiddenInput
              onChooseFiles={appendSelectedFiles}
              selectables={["file"]}
              multiple={multiple}
              kind="button"
              inputRef={fileInputRef}
            />
          )}
          {selectables.includes("folder") && (
            <HiddenInput
              onChooseFiles={appendSelectedFiles}
              selectables={["folder"]}
              multiple={multiple}
              kind="button"
              inputRef={folderInputRef}
            />
          )}
        </div>
        <HiddenInput
          onChooseFiles={appendSelectedFiles}
          selectables={selectables}
          multiple={multiple}
          kind="drag-drop"
        />
      </div>
    </div>
  );
};

export default UploadStuff;
